import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { SkeletonRows, ErrorMessage, usePageMeta } from '../components/ui';

const pct = (x, digits = 1) => (x == null ? '—' : `${(x * 100).toFixed(digits)}%`);
const pts = (x) => (x == null ? '—' : `${x >= 0 ? '+' : ''}${(x * 100).toFixed(1)}`);
/* The same analysis for both special forms. Every hero costs the same 200
   Hero Coins; evolutions are unlocked with Evolution Shards. */
const FORMS = {
  hero: {
    path: '/clash/heroes',
    load: () => api.getClashHeroes(),
    title: 'Which Clash Royale Hero to Buy',
    description: 'Every Clash Royale hero ranked by how much win rate it adds over the plain card, measured from tracked battles and refreshed weekly.',
    h1: 'Which Hero Should I Buy?',
    intro: 'Every hero costs 200 Hero Coins. This ranks them',
    prefix: 'Hero',
    noun: 'hero',
    tab: 'Heroes',
  },
  evo: {
    path: '/clash/evolutions',
    load: () => api.getClashEvos(),
    title: 'Which Clash Royale Evolution to Unlock',
    description: 'Every Clash Royale evolution ranked by how much win rate it adds over the plain card, measured from tracked battles and refreshed weekly.',
    h1: 'Which Evolution Should I Unlock?',
    intro: 'Evolution Shards are scarce. This ranks every evolution',
    prefix: 'Evo',
    noun: 'evolution',
    tab: 'Evolutions',
  },
};

const VERDICT_CLASS = { 'Clear gain': 'up', 'Likely gain': 'up', 'No clear difference': 'flat', 'Worse than plain': 'down' };

/* Hero art from Supercell's asset CDN, as the API links it. Falls back to the
   first letter on a tile when the report has no art or the image won't load. */
function HeroArt({ hero, prefix }) {
  const [broken, setBroken] = useState(false);
  return (
    <span className="av card-art">
      {hero.icon && !broken ? (
        <img src={hero.icon} alt={`${prefix} ${hero.name}`} loading="lazy" onError={() => setBroken(true)} />
      ) : (
        <span className="av-fb">{hero.name.charAt(0)}</span>
      )}
    </span>
  );
}

/* Which hero to spend Hero Coins on, or which evolution to unlock. The list is
   ranked by what the form adds over the same card played plain, after taking
   out the card-level matchup. Built weekly from Trophy Road and ranked battles. */
function ClashHeroes({ form = 'hero' }) {
  const f = FORMS[form];
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [nonce, setNonce] = useState(0);
  const [sort, setSort] = useState({ key: 'score', dir: -1 });
  usePageMeta({ title: f.title, description: f.description });

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setData(null);
    f.load()
      .then((d) => { if (!cancelled) setData(d); })
      .catch((err) => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [nonce, f]);

  const rows = useMemo(() => {
    const list = data?.heroes || [];
    return list.slice().sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;   // no data sinks, whichever way you sort
      if (bv == null) return -1;
      if (typeof av === 'string') return av.localeCompare(bv) * sort.dir;
      return (av - bv) * sort.dir;
    });
  }, [data, sort]);

  const th = (key, label, title) => (
    <th
      className="sortable"
      title={title}
      onClick={() => setSort((s) => ({ key, dir: s.key === key ? -s.dir : -1 }))}
    >
      {label}
      {sort.key === key && <span className="arr">{sort.dir < 0 ? '▼' : '▲'}</span>}
    </th>
  );

  const top = data?.heroes?.find((h) => h.enough_data);

  return (
    <div className="page">
      <div className="wrap">
        <div className="phead">
          <div>
            <div className="eyebrow">Clash Royale · updated weekly</div>
            <h1>{f.h1}</h1>
            <div className="sub">
              {f.intro} by how much it actually adds to your win rate,
              measured from {data ? `${(data.ladder_battles + data.ranked_battles).toLocaleString()} ` : ''}real
              battles{data ? ` over the last ${data.window_days} days` : ''}.
            </div>
          </div>
          <div className="seg" role="tablist">
            {Object.entries(FORMS).map(([key, o]) => (
              <button key={key} role="tab" aria-selected={key === form} className={key === form ? 'on' : ''}
                onClick={() => navigate(o.path)}>
                {o.tab}
              </button>
            ))}
          </div>
        </div>

        {error ? (
          <ErrorMessage message={error} onRetry={() => setNonce((n) => n + 1)} />
        ) : !data ? (
          <SkeletonRows rows={8} />
        ) : (
          <>
            {top && (
              <div className="stat-ribbon">
                <div className="srib"><div className="v">{top.name}</div><div className="l">Best buy right now</div></div>
                <div className="srib"><div className="v">{pts(top.uplift)} pts</div><div className="l">Win rate it adds</div></div>
                <div className="srib"><div className="v">{pct(top.adj_win_rate)}</div><div className="l">Its adjusted win rate</div></div>
                <div className="srib"><div className="v">{data.generated_at.slice(0, 10)}</div><div className="l">Last refreshed</div></div>
              </div>
            )}

            <div className="tablecard">
              <div className="table-scroll">
                <table className="meta">
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}>#</th>
                      {th('name', f.prefix === 'Evo' ? 'Evolution' : 'Hero')}
                      {th('score', 'Adds', `Win-rate points the ${f.noun} adds over the same card played plain, after removing the level matchup`)}
                      {th('adj_win_rate', 'Win rate', `${f.prefix} win rate, adjusted for card levels on both sides`)}
                      {th('plain_adj_win_rate', 'Plain card', 'Same card without the hero, adjusted the same way')}
                      {th('ranked_win_rate', 'Ranked', 'Path of Legends win rate, where card levels are equal for everyone')}
                      {th('use_rate', 'Played', `Share of Trophy Road decks playing this ${f.noun}`)}
                      {th('hero_share', `As ${f.prefix.toLowerCase()}`, `Of Trophy Road decks running this card, the share playing it as ${form === 'evo' ? 'an evolution' : 'a hero'}`)}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((h, i) => (
                      <tr key={h.id}>
                        <td className="rank">{i + 1}</td>
                        <td style={{ minWidth: 220 }}>
                          <div className="pcell">
                            <HeroArt hero={h} prefix={f.prefix} />
                            <div>
                              <div className="pn" style={{ whiteSpace: 'nowrap' }}>{f.prefix} {h.name}</div>
                              <div className="pt">
                                {h.elixir != null ? `${h.elixir} elixir · ` : ''}
                                {h.plays.toLocaleString()} games
                                {h.partners?.length > 0 && ` · often with ${h.partners.slice(0, 3).map((p) => p.name).join(', ')}`}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {h.uplift == null ? (
                            <span className="num-cell">—</span>
                          ) : (
                            <>
                              <span className={`trend ${VERDICT_CLASS[h.verdict] || 'flat'}`}>{pts(h.uplift)}</span>
                              <span className="num-cell"> ±{(h.uplift_se * 196).toFixed(1)}</span>
                              <div className="pt">{h.verdict}</div>
                            </>
                          )}
                        </td>
                        <td>
                          <span className="num-cell">{pct(h.adj_win_rate)}</span>
                          {h.tiers && Object.keys(h.tiers).length > 1 && (
                            <div className="pt">
                              {Object.entries(h.tiers).map(([tier, s]) => `${tier} ${pct(s.adj_win_rate, 0)}`).join(' · ')}
                            </div>
                          )}
                        </td>
                        <td><span className="num-cell">{pct(h.plain_adj_win_rate)}</span></td>
                        <td><span className="num-cell">{pct(h.ranked_win_rate)}</span></td>
                        <td><span className="num-cell">{pct(h.use_rate)}</span></td>
                        <td><span className="num-cell">{pct(h.hero_share, 0)}</span></td>
                      </tr>
                    ))}
                    {rows.length === 0 && (
                      <tr><td colSpan={8} className="empty-state">No {f.noun} data in this window yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="tip">
              <b>How to read this.</b> <b>Adds</b> is the number to decide on: how many win-rate points the {f.noun}{' '}
              wins over the same card played without it. {form === 'evo' ? 'An' : 'A'} {f.noun} on a card that's already strong adds nothing
              if the plain card wins just as often. Trophy Road numbers are adjusted for card levels, because{' '}
              {f.noun} owners tend to have stronger decks; Path of Legends levels are equal, so its win rate needs no
              adjustment and counts as a second, independent read. {f.tab} with fewer than {data.min_plays} games
              in the window show no numbers. Mirror matches are left out.
              {data.short_deck_share > 0.02 && (
                <> {pct(data.short_deck_share)} of decks came back from the API with a card missing, likely a
                hero it can't display, so those games aren't counted.</>
              )}
            </div>
            <div className="tip">
              {form === 'hero' && (
                <>Want the reasoning? <a href="/reports/which-clash-royale-hero-to-buy-october-2026.html">Read the October 2026 hero report →</a>{' '}</>
              )}
              Got your {f.noun}? <Link to="/clash">See which card to upgrade next →</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ClashHeroes;
